import type { ComponentProps, ReactNode } from 'react'
import { useMemo } from 'react'
import getOfferFeatureIcon from 'lib/offer/getOfferFeatureIcon'
import formatOfferInclusionDescription from 'lib/offer/formatOfferInclusionDescription'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import type { SvgIconComponent, SvgIconProps } from 'LuxKit/SvgIcon'
import getInclusionMinStayText from 'lib/offer/getInclusionMinStayText'
import { useIntl } from 'react-intl'
import Colour from 'components/utils/Colour'
import TourCampaignFlightCreditDescription from 'components/Tours/TourCampaignFlightCreditDescription'
import { TOUR_CAMPAIGN_FLIGHT_CREDIT_INCLUSION_ID } from 'constants/tours'

type BodyTextBlockProps = ComponentProps<typeof BodyTextBlock>

type OfferInclusionItemInclusion = App.OfferInclusion & {
  colour?: BodyTextBlockProps['colour']
  iconColour?: SvgIconProps['colour']
  Icon?: SvgIconComponent
}

interface Props {
  inclusion: OfferInclusionItemInclusion
  isHighlighted?: boolean
  /**
   * @default medium
   */
  size?: BodyTextBlockProps['variant']
  format?: BodyTextBlockProps['format']
  colour?: BodyTextBlockProps['colour']
  lineClamp?: BodyTextBlockProps['lineClamp']
  iconSize?: SvgIconProps['size']
  /**
   * @default default
   */
  type?: 'default' | 'lux-plus'
  showMinStayDescription?: boolean
  endIcon?: ReactNode
}

function OfferInclusionItem({
  inclusion,
  isHighlighted,
  size = 'medium',
  format,
  colour,
  lineClamp,
  iconSize,
  type,
  showMinStayDescription,
  endIcon,
}: Props) {
  const intl = useIntl()
  const Icon = useMemo(() => {
    if (inclusion.Icon) return inclusion.Icon
    return getOfferFeatureIcon(inclusion.symbol)
  }, [inclusion.Icon, inclusion.symbol])
  const highlighted = isHighlighted ?? inclusion.isHighlighted
  const contentColour = colour ?? inclusion.colour
  const iconColour: SvgIconProps['colour'] = contentColour ?? inclusion.iconColour ?? (type === 'lux-plus' ? 'lux-plus' : highlighted ? 'highlight-primary-dark' : undefined)
  const minStayDescription = showMinStayDescription ? getInclusionMinStayText(inclusion, intl) : undefined
  const inclusionDescription = useMemo(() => {
    return formatOfferInclusionDescription(inclusion.description, intl)
  }, [inclusion.description, intl])

  const describedInclusion = inclusion.id === TOUR_CAMPAIGN_FLIGHT_CREDIT_INCLUSION_ID ?
      <TourCampaignFlightCreditDescription /> :
    minStayDescription ?
      `${inclusionDescription} ${minStayDescription}` :
      inclusionDescription

  return <BodyTextBlock
    variant={size}
    format={format}
    colour={colour}
    lineClamp={lineClamp}
    weight={highlighted ? 'bold' : 'normal'}
    startIcon={<Icon colour={iconColour} data-symbol={inclusion.symbol} data-testid={inclusion.Icon ? inclusion.id : undefined} size={iconSize} />}
    endIcon={endIcon}
  >
    {contentColour && !colour ? <Colour value={contentColour}>{describedInclusion}</Colour> : describedInclusion}
  </BodyTextBlock>
}

export default OfferInclusionItem
